import React, { useEffect, useState } from "react";
import {
  Modal,
  Text,
  Stack,
  Paper,
  Group,
  Badge,
  Table,
  ScrollArea,
  Divider,
} from "@mantine/core";
import { IconHistory } from "@tabler/icons-react";
import { ApiService } from "../../api/api-service";
import { formatDecimalHoursToTime } from "./InstructorForm";

interface AttendedHoursLogEntry {
  log_id: string;
  timestamp: string;
  trainerName: string;
  trainerEmail: string;
  previousHours: number;
  incomingHours: number;
  newHours: number;
  matchedByTrainerEmail: boolean | null;
  description: string;
  updatedByName?: string;
  updatedByEmail?: string;
  source?: string;
}

interface Props {
  isOpen: boolean;
  batchNo: string | null;
  onClose: () => void;
}

const AttendedHoursLogsModal: React.FC<Props> = ({
  isOpen,
  batchNo,
  onClose,
}) => {
  const [entries, setEntries] = useState<AttendedHoursLogEntry[]>([]);
  const [latestTotalHours, setLatestTotalHours] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && batchNo) fetchLogs(batchNo);
  }, [isOpen, batchNo]);

  const fetchLogs = async (batch_no: string) => {
    try {
      setLoading(true);
      const res = await ApiService.getInstance().get(
        `/batches/attended-hours-logs/${encodeURIComponent(batch_no)}`
      );
      setEntries(res.data.data || []);
      setLatestTotalHours(res.data.latestTotalHours || 0);
    } catch (error) {
      console.error("Error fetching attended hours logs:", error);
      setEntries([]);
      setLatestTotalHours(0);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap={6}>
          <IconHistory size={18} />
          <Text fw={600}>Attended Hours Log – Batch {batchNo}</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="sm">
        <Paper withBorder p="sm" radius="md" bg="blue.0">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Current Total Attended Hours
            </Text>
            <Text size="lg" fw={700} c="blue.8">
              {formatDecimalHoursToTime(latestTotalHours) || "0h"} ({latestTotalHours.toFixed(2)} hrs)
            </Text>
          </Group>
        </Paper>

        <Divider label="History (oldest → newest)" labelPosition="center" />

        <ScrollArea h={380}>
          {loading ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              Loading...
            </Text>
          ) : entries.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              No attended-hours updates recorded for this batch yet
            </Text>
          ) : (
            <Table striped highlightOnHover verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Trainer</Table.Th>
                  <Table.Th>Update</Table.Th>
                  <Table.Th>Updated By</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {entries.map((entry) => (
                  <Table.Tr key={entry.log_id}>
                    <Table.Td>
                      <Text size="xs">{formatDate(entry.timestamp)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{entry.trainerName || "—"}</Text>
                        {entry.matchedByTrainerEmail === false && (
                          <Badge size="xs" color="yellow" variant="light">
                            fallback match
                          </Badge>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {entry.previousHours.toFixed(2)} + {entry.incomingHours.toFixed(2)}{" "}
                        <Text span fw={700} c="blue.8">
                          = {entry.newHours.toFixed(2)}
                        </Text>
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDecimalHoursToTime(entry.previousHours) || "0h"} →{" "}
                        {formatDecimalHoursToTime(entry.newHours) || "0h"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">
                          {entry.updatedByName || "LMS Webhook"}
                        </Text>
                        {entry.source === "LMS_WEBHOOK" && (
                          <Badge size="xs" color="gray" variant="light">
                            legacy LMS
                          </Badge>
                        )}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </ScrollArea>
      </Stack>
    </Modal>
  );
};

export default AttendedHoursLogsModal;
